import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import styled from "styled-components";
import isHotkey from "is-hotkey";
import { Editable, withReact, useSlate, Slate } from "slate-react";
import { Editor, Transforms, Range, createEditor } from "slate";
import { withHistory } from "slate-history";
import produce from "immer";
import isUrl from "is-url";
import HeadingSelector from "./HeadingSelector";
import LinkPrompt from "./LinkPrompt";
import { useDebounce } from "use-lodash-debounce";
import HelpIcon from "../common3/HelpBox/Icon";
import HelpBox from "../common3/HelpBox";
import get from "lodash/get";
import ValidationBar from "../common3/ValidationBar";

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 5px;

  ${(p) =>
    p.portalEdit &&
    `
    padding: 15px;
    border: 1px solid #ddd;
    border-radius: 6px;
  `}
`;

const EditorWrapper = styled.div`
  border: 1px solid #ddd;
  background-color: white;
  padding: 6px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
`;

const ScrollWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  padding: 20px;
  overflow-x: auto;
  overflow-y: hidden;
  transition: transform 150ms ease, opacity 150ms ease;
  transform: scale(${(p) => (p.state === "entered" ? "1" : "0")});
  opacity: ${(p) =>
    p.state === "entered" ? "1" : p.state === "entering" ? "1" : "0"};
`;

const StyledEditable = styled(Editable)`
  padding-top: 10px;
`;

const H1 = styled.h1`
  margin: 16px 0;
  text-align: ${(p) => p.align};
`;

const H2 = styled.h2`
  margin: 16px 0;
  text-align: ${(p) => p.align};
`;

const H3 = styled.h3`
  margin: 16px 0;
  text-align: ${(p) => p.align};
`;

const H4 = styled.h4`
  margin: 16px 0;
  text-align: ${(p) => p.align};
`;

const H5 = styled.h5`
  margin: 16px 0;
  text-align: ${(p) => p.align};
`;

const H6 = styled.h6`
  margin: 16px 0;
  text-align: ${(p) => p.align};
`;

const P = styled.p`
  margin: 16px 0;
  text-align: ${(p) => p.align};
`;

const Code = styled.code`
  padding: 3px;
  background-color: #eee;
`;

const BlockQuote = styled.blockquote`
  margin: 16px 40px;
  padding-left: 10px;
  border-left: 2px solid #ddd;
  color: #aaa;
  text-align: ${(p) => p.align};
`;

const UL = styled.ul`
  margin: 16px 0;
  padding-left: 20px;

  & > li {
    text-align: ${(p) => p.align};
  }
`;

const OL = styled.ol`
  margin: 16px 0;
  padding-left: 20px;

  & > li {
    text-align: ${(p) => p.align};
  }
`;

const LI = styled.li`
  text-align: ${(p) => p.align};
`;

const Icon = styled.em`
  color: ${(p) => (p.active ? "#06c" : "rgb(204, 204, 204)")};
`;

const LinkButtonWrapper = styled.div`
  position: relative;
`;

const LinkButtonHelpText = styled.p``;

const Button = styled.button`
  margin: 0 10px;
  padding: 10px;
  border: none;
  font-size: 16px;
  cursor: pointer;
  background-color: transparent;

  &:hover {
    & > ${Icon} {
      color: #06c;
    }
  }
`;

const HOTKEYS = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
  "mod+`": "code",
};

const LIST_TYPES = ["numbered-list", "bulleted-list"];

const initialValue = [
  {
    type: "paragraph",
    align: "left",
    children: [{ text: "" }],
  },
];

const TextEditor = (props) => {
  const {
    extra = {},
    state,
    portalEdit = false,
    htmlTemplates = {},
    validationError = {},
    disableValidation = false,
    intl,
  } = props;
  const {
    text = initialValue,
    helpBoxAlwaysVisible = false,
    helpBoxIcon = false,
    helpBoxTemplateId = null,
    helpBoxTemplate = null,
  } = extra;

  const [value, setValue] = useState(
    portalEdit ? (props.value ? props.value : initialValue) : text
  );
  const debouncedValue = useDebounce(value, 500, { trailing: true });
  const [helpBoxOpen, setHelpBoxOpen] = useState(
    helpBoxAlwaysVisible ? true : false
  );

  const renderElement = useCallback((props) => <Element {...props} />, []);
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
  const editor = useMemo(
    () => withLinks(withHistory(withReact(createEditor()))),
    []
  );

  const [selectedBlockIndex, setSelectedBlockIndex] = useState(0);
  const [activeAlign, setActiveAlign] = useState(value[0].align);

  const [editorSelection, setEditorSelection] = useState();
  const [linkPromptOpen, setLinkPromptOpen] = useState(false);

  const alignBlock = useCallback((position) => {
    const newValue = produce(value, (draft) => {
      draft[selectedBlockIndex].align = position;
    });

    setActiveAlign(position);
    setValue(newValue);
  });

  const setAlignAndSelection = useCallback(() => {
    if (editor.selection) {
      const blockIndex = editor.selection.anchor.path[0];
      setSelectedBlockIndex(blockIndex);
      const align = editor.children[blockIndex].align;
      setActiveAlign(align);
    }
  }, []);

  const didMountRef = useRef(false);

  useEffect(() => {
    if (didMountRef.current) {
      if (portalEdit) {
        props.save({ key: props.id, value: debouncedValue });
        return;
      }

      props.save({ _id: props.id, key: "text", value: debouncedValue });
    } else {
      didMountRef.current = true;
    }
  }, [debouncedValue]);

  const _helpBoxTemplate =
    helpBoxTemplateId === "_custom"
      ? helpBoxTemplate
      : get(htmlTemplates, [helpBoxTemplateId, "html"], "");

  const errorMessage = validationError.critical;

  return (
    <div>
      <>
        {portalEdit && !helpBoxAlwaysVisible && helpBoxIcon && (
          <HelpIcon toggle={() => setHelpBoxOpen(!helpBoxOpen)} />
        )}
        <EditorWrapper>
          <Slate
            editor={editor}
            value={value}
            onChange={(value) => setValue(value)}
          >
            <Wrapper
              data-id={props.id}
              onClick={(e) => {
                e.stopPropagation();
              }}
              portalEdit={portalEdit}
            >
              <Toolbar /* state={state} */>
                <HeadingButton
                  selectedBlock={value[selectedBlockIndex]}
                  selection={editorSelection}
                  setSelection={setEditorSelection}
                />
                <ScrollWrapper state={state}>
                  <MarkButton
                    format="bold"
                    icon="far fa-bold"
                    ariaLabel="Bold"
                  />
                  <MarkButton
                    format="italic"
                    icon="far fa-italic"
                    ariaLabel="Italic"
                  />
                  <MarkButton
                    format="underline"
                    icon="far fa-underline"
                    ariaLabel="Underline"
                  />
                  <MarkButton
                    format="code"
                    icon="far fa-code"
                    ariaLabel="Code"
                  />
                  <LinkButton
                    icon="far fa-link"
                    selection={editorSelection}
                    setSelection={setEditorSelection}
                    open={linkPromptOpen}
                    setOpen={setLinkPromptOpen}
                    ariaLabel="Link"
                  />

                  <AlignButton
                    active={activeAlign === "left"}
                    alignBlock={alignBlock}
                    format="left"
                    icon="far fa-align-left"
                    ariaLabel="Align left"
                  />
                  <AlignButton
                    active={activeAlign === "center"}
                    alignBlock={alignBlock}
                    format="center"
                    icon="far fa-align-center"
                    ariaLabel="Align center"
                  />
                  <AlignButton
                    active={activeAlign === "right"}
                    alignBlock={alignBlock}
                    format="right"
                    icon="far fa-align-right"
                    ariaLabel="Align right"
                  />

                  <BlockButton
                    format="block-quote"
                    icon="far fa-quote-right"
                    ariaLabel="Quote"
                  />
                  <BlockButton
                    format="numbered-list"
                    icon="far fa-list-ol"
                    ariaLabel="Ordered list"
                  />
                  <BlockButton
                    format="bulleted-list"
                    icon="far fa-list-ul"
                    ariaLabel="Unordered list"
                  />
                </ScrollWrapper>
              </Toolbar>
              {
                <StyledEditable
                  renderElement={renderElement}
                  renderLeaf={renderLeaf}
                  spellCheck
                  autoFocus
                  onKeyDown={(event) => {
                    for (const hotkey in HOTKEYS) {
                      if (isHotkey(hotkey, event)) {
                        event.preventDefault();
                        const mark = HOTKEYS[hotkey];
                        toggleMark(editor, mark);
                      }
                    }
                  }}
                  onKeyUp={() => setAlignAndSelection()}
                  onClick={() => setAlignAndSelection()}
                />
              }
            </Wrapper>
          </Slate>
        </EditorWrapper>
        {portalEdit &&
          (helpBoxAlwaysVisible || helpBoxIcon) &&
          _helpBoxTemplate &&
          helpBoxOpen && (
            <HelpBox template={_helpBoxTemplate} props={props} value={value} />
          )}
        <ValidationBar
          error={errorMessage}
          disableValidation={disableValidation}
        />
      </>
      {isLinkActive(editor) && (
        <LinkButtonHelpText>
          {intl.formatMessage({ id: "Link.helpText" })}
        </LinkButtonHelpText>
      )}
    </div>
  );
};

const withLinks = (editor) => {
  const { insertData, insertText, isInline } = editor;

  editor.isInline = (element) => {
    return element.type === "link" ? true : isInline(element);
  };

  editor.insertText = (text) => {
    if (text && isUrl(text)) {
      wrapLink(editor, text);
    } else {
      insertText(text);
    }
  };

  editor.insertData = (data) => {
    const text = data.getData("text/plain");

    if (text && isUrl(text)) {
      wrapLink(editor, text);
    } else {
      insertData(data);
    }
  };

  return editor;
};

const insertLink = (editor, url) => {
  if (editor.selection) {
    wrapLink(editor, url);
  }
};

const isLinkActive = (editor) => {
  const [link] = Editor.nodes(editor, { match: (n) => n.type === "link" });
  return !!link;
};

const unwrapLink = (editor) => {
  Transforms.unwrapNodes(editor, { match: (n) => n.type === "link" });
};

const wrapLink = (editor, url) => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link = {
    type: "link",
    url,
    children: isCollapsed ? [{ text: url }] : [],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: "end" });
  }
};

const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(editor, format);
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: (n) => LIST_TYPES.includes(n.type),
    split: true,
  });

  Transforms.setNodes(editor, {
    type: isActive ? "paragraph" : isList ? "list-item" : format,
  });

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const isBlockActive = (editor, format) => {
  const [match] = Editor.nodes(editor, {
    match: (n) => n.type === format,
  });

  return !!match;
};

const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

const Element = ({ attributes, children, element }) => {
  switch (element.type) {
    case "block-quote":
      return (
        <BlockQuote align={element.align} {...attributes}>
          {children}
        </BlockQuote>
      );
    case "bulleted-list":
      return (
        <UL align={element.align} {...attributes}>
          {children}
        </UL>
      );
    case "heading-one":
      return (
        <H1 align={element.align} {...attributes}>
          {children}
        </H1>
      );
    case "heading-two":
      return (
        <H2 align={element.align} {...attributes}>
          {children}
        </H2>
      );
    case "heading-three":
      return (
        <H3 align={element.align} {...attributes}>
          {children}
        </H3>
      );
    case "heading-four":
      return (
        <H4 align={element.align} {...attributes}>
          {children}
        </H4>
      );
    case "heading-five":
      return (
        <H5 align={element.align} {...attributes}>
          {children}
        </H5>
      );
    case "heading-six":
      return (
        <H6 align={element.align} {...attributes}>
          {children}
        </H6>
      );
    case "list-item":
      return <LI {...attributes}>{children}</LI>;
    case "numbered-list":
      return (
        <OL align={element.align} {...attributes}>
          {children}
        </OL>
      );
    case "link":
      return (
        <a {...attributes} href={element.url}>
          {children}
        </a>
      );
    default:
      return (
        <P align={element.align} {...attributes}>
          {children}
        </P>
      );
  }
};

const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.code) {
    children = <Code>{children}</Code>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  return <span {...attributes}>{children}</span>;
};

const BlockButton = ({ format, icon, ariaLabel }) => {
  const editor = useSlate();

  return (
    <Button
      onMouseDown={(event) => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
      aria-label={ariaLabel}
    >
      <Icon className={icon} active={isBlockActive(editor, format)} />
    </Button>
  );
};

const AlignButton = ({
  active = false,
  alignBlock,
  format,
  icon,
  ariaLabel,
}) => {
  return (
    <Button
      onMouseDown={(event) => {
        event.preventDefault();
        alignBlock(format);
      }}
      aria-label={ariaLabel}
    >
      <Icon active={active} className={icon} />
    </Button>
  );
};

const HeadingButton = ({ selectedBlock, selection, setSelection }) => {
  const editor = useSlate();

  const selectHeading = (format) => {
    editor.selection = selection;
    toggleBlock(editor, format);
  };

  return (
    <div
      onMouseDown={(event) => {
        event.preventDefault();
        setSelection(editor.selection);
      }}
    >
      <HeadingSelector
        selectedBlock={selectedBlock}
        selectHeading={selectHeading}
      />
    </div>
  );
};

const LinkButton = ({
  icon,
  selection,
  setSelection,
  open,
  setOpen,
  ariaLabel,
}) => {
  const editor = useSlate();

  const submitLink = (url) => {
    if (!url) {
      return;
    }

    editor.selection = selection;

    insertLink(editor, url);
  };

  const toggleInputModal = (event) => {
    event.preventDefault();
    let url;
    const editorSelection = editor.selection;
    const parentNode = Editor.parent(editor, editorSelection);
    const node = Editor.node(editor, editorSelection);
    if (node[0]?.type === "paragraph") {
      // This is super harcoded but this is for when text component includes only a link.
      // I think this should work always but it is not pretty.
      url = node[0]?.children[1]?.url;
    } else {
      url = parentNode[0]?.url || "";
    }
    const _selection = { ...editorSelection, url };
    setSelection(_selection);
    setOpen((prevState) => !prevState);
  };

  return (
    <LinkButtonWrapper>
      <Button
        onClick={(event) => toggleInputModal(event)}
        aria-label={ariaLabel}
      >
        <Icon active={isLinkActive(editor)} className={icon} />
      </Button>
      {open ? (
        <LinkPrompt
          setOpen={setOpen}
          submitLink={submitLink}
          url={selection?.url}
        />
      ) : (
        ""
      )}
    </LinkButtonWrapper>
  );
};

const MarkButton = ({ format, icon, ariaLabel }) => {
  const editor = useSlate();
  return (
    <Button
      onMouseDown={(event) => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
      aria-label={ariaLabel}
    >
      <Icon className={icon} active={isMarkActive(editor, format)} />
    </Button>
  );
};

export default TextEditor;
